import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { faFile, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Heading, Text, TextButton, Tooltip } from '@modusign/design-system';
import { getGetDocumentListQueryKey } from '@modusign/openapi-client-external-document';
import { useReducer } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle, useUpdateEffect } from 'react-use';

import LoadingStatus from '@libs/loading-status';

import {
  WindowModal,
  WindowModalContent,
  WindowModalFooter,
} from '@app/components/Modal/WindowModal';
import { LabelListSummary } from '@app/features/label';
import usePreventUnload from '@app/hooks/usePreventUnload';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';

import {
  confirmManagementTooltip,
  getExternalDocumentUploadingDocuments,
  getExternalDocumentUploadStatus,
  getIsModalMinimized,
  getIsNotIdleStatus,
  getUploadingSummary,
  initializeExternalDocumentUpload,
  retryFailedDocuments,
  toggleModalMinimize,
} from '../../state';

export default function DocumentUploadModal() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  const [progressBarKey, changeProgressBarKey] = useReducer((x) => x + 1, 0);

  const isOpen = useSelector(getIsNotIdleStatus);
  const isMinimized = useSelector(getIsModalMinimized);

  const status = useSelector(getExternalDocumentUploadStatus);
  const summary = useSelector(getUploadingSummary);
  const documents = useSelector(getExternalDocumentUploadingDocuments);

  usePreventUnload(summary.idle !== 0 || summary.pending !== 0);

  useUpdateEffect(() => {
    if (isOpen) {
      queryClient.invalidateQueries(getGetDocumentListQueryKey(workspaceId));
    }
  }, [summary]);

  // 업로드가 완료되면 성공 이벤트를 어도비 데이터레이어에 push 합니다.
  useUpdateEffect(() => {
    if (status === LoadingStatus.SUCCEEDED) {
      pushAdobeDataLayer({
        event: 'createExternalDocuments',
        numberOfCreatedExternalDocuments: summary.succeeded,
      });
    }
  }, [status]);

  const toggleMinimize = () => dispatch(toggleModalMinimize());

  if (!isOpen) {
    return null;
  }

  if (isMinimized) {
    return null;
  }

  // 모두 같은 라벨을 적용하기 때문에 첫번째 문서의 라벨을 보여줍니다.
  const { labels } = documents[0];

  return (
    <WindowModal isOpen maxWidth="md">
      <Flex
        alignItems="center"
        backgroundColor="lightGray50"
        borderBottom="1px solid"
        borderBottomColor="lightGray300"
        borderTopLeftRadius="3px"
        borderTopRightRadius="3px"
        justifyContent="space-between"
        p={6}
        width="100%"
      >
        <Heading
          as="h1"
          fontSize="md"
          fontWeight="medium"
          sx={{
            color: 'textNeutralLight',
          }}
        >
          <FontAwesomeIcon icon={faFile} /> 계약 등록
        </Heading>
        <Tooltip content="작게 보기" placement="top">
          <Box
            aria-label="작게보기"
            as="button"
            color="textNeutralLight"
            fontSize="18px"
            type="button"
            width="72px"
            onClick={() => {
              toggleMinimize();
              // 모달 최소화시 5초후에 계약 관리 안내 툴팁을 끕니다.
              setTimeout(() => dispatch(confirmManagementTooltip()), 5000);
            }}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </Box>
        </Tooltip>
      </Flex>
      <WindowModalContent>
        <Flex justifyContent="space-between">
          <Box>
            <Text fontSize="lg" fontWeight="medium">
              완료 {summary.succeeded}건
            </Text>
            {summary.failed > 0 && (
              <>
                <Text color="textNeutralVeryLight" fontSize="lg" fontWeight="medium" mx={2}>
                  /
                </Text>
                <Text color="textNegativeBase" fontSize="lg" fontWeight="medium">
                  실패 {summary.failed}건
                </Text>
              </>
            )}
          </Box>
          <Text fontSize="lg" fontWeight="medium">
            총 {documents.length}건
          </Text>
        </Flex>
        <Box
          bg="statusBackgroundNeutralBase"
          borderRadius="9px"
          height="18px"
          key={progressBarKey}
          mt={4}
          overflow="hidden"
          width="100%"
        >
          <Box
            bg="statusBackgroundPrimaryBase"
            borderRadius="9px"
            height="100%"
            style={{
              width: `${Math.floor(
                ((summary.failed + summary.succeeded + summary.pending / 2) / documents.length) *
                  100
              )}%`,
              transition: 'width 0.8s ease-out',
            }}
          />
        </Box>
        {labels.length > 0 && (
          <Text color="textNeutralLight" display="flex" mt={6} whiteSpace="pre">
            <LabelListSummary fontSize="md" labelIds={labels.map((l) => l.id)} />
            {labels.length > 1 ? '의' : ''} 라벨이 적용됩니다.
          </Text>
        )}
        <Box border="1px solid" borderColor="tableBorderNeutralBase" mt={6}>
          <Flex
            alignItems="center"
            bg="tableBackgroundNeutralSlightlyDark"
            borderBottom="1px solid"
            borderBottomColor="tableBorderNeutralBase"
            px={4}
            py={3}
          >
            <Text color="textNeutralVeryLight" sx={{ flex: 'auto' }}>
              계약 제목
            </Text>
            <Text color="textNeutralVeryLight" sx={{ width: '133px', px: 4 }}>
              상태
            </Text>
          </Flex>
          <Box as="ol" height="321px" overflow="auto">
            {documents.map(({ error, name, status }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem error={error} key={index} name={name} status={status} />
            ))}
          </Box>
        </Box>
      </WindowModalContent>
      <WindowModalFooter display="flex" justifyContent="flex-end">
        {status === LoadingStatus.SUCCEEDED && summary.failed !== 0 && (
          <Button
            variant="basic"
            onClick={() => {
              dispatch(retryFailedDocuments());
              changeProgressBarKey();
            }}
          >
            다시 시도
          </Button>
        )}
        <Button
          disabled={status !== LoadingStatus.SUCCEEDED}
          ml={3}
          variant="primary"
          onClick={() => {
            dispatch(initializeExternalDocumentUpload());
            // 확인시 5초후에 계약 관리 안내 툴팁을 끕니다.
            setTimeout(() => dispatch(confirmManagementTooltip()), 5000);
          }}
        >
          확인
        </Button>
      </WindowModalFooter>
    </WindowModal>
  );
}

function ListItem({ name, status, error }: { name: string; status: LoadingStatus; error?: Error }) {
  const [isErrorDetailOpen, toggleErrorDetail] = useToggle(false);

  return (
    <Box as="li">
      <Flex
        alignItems="center"
        borderBottom="1px solid"
        borderBottomColor="tableBorderNeutralBase"
        p={4}
      >
        <Text
          sx={{
            flex: 'auto',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            pr: 4,
          }}
        >
          {name}
        </Text>
        <Text sx={{ width: '62px', flex: 'none', pl: 4 }}>
          <StatusBadge status={status} />
        </Text>
        {error ? (
          <TextButton sx={{ width: '71px', flex: 'none' }} onClick={toggleErrorDetail}>
            자세히 <FontAwesomeIcon icon={isErrorDetailOpen ? faChevronUp : faChevronDown} />
          </TextButton>
        ) : (
          <Box flex="none" width="71px" />
        )}
      </Flex>
      {isErrorDetailOpen && error && (
        <Box bg="lightGray50" px={5} py={7}>
          등록 실패 사유:{' '}
          {coalesceGlobalErrorMessage(error.message, {
            'Document category not found':
              '선택한 계약 유형이 없습니다. 확인 후 다시 시도해 주세요. 그래도 선택할 수 없다면 삭제되었을 수 있습니다.',
            'Label not found':
              '선택한 라벨이 없습니다. 확인 후 다시 시도해 주세요. 그래도 선택할 수 없다면 삭제되었을 수 있습니다.',
            'A requested file or directory could not be found at the time an operation was processed.':
              '제거되었거나 이동된 파일입니다.',
            'Usage count exceeded throttle limit': '외부문서 사용량을 초과했습니다.',
          })}
        </Box>
      )}
    </Box>
  );
}

function StatusBadge({ status }: { status: LoadingStatus }) {
  switch (status) {
    case LoadingStatus.IDLE:
      return <Text>대기 중</Text>;
    case LoadingStatus.PENDING:
      return <Text>등록 중</Text>;
    case LoadingStatus.SUCCEEDED:
      return <Text>완료</Text>;
    case LoadingStatus.FAILED:
      return (
        <Text
          color="textNegativeBase"
          sx={{ textDecoration: 'underline', textUnderlineOffset: '0.2em' }}
        >
          실패
          <Box as="span">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              style={{ fontSize: '11px', marginLeft: '2px' }}
            />
          </Box>
        </Text>
      );
    default:
      return null;
  }
}
